import { useEffect, useState } from "react";

import { UseFormReturn } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import Form from "../../components/base/Form/Form";
import { FormTextField } from "../../components/base/Form/FormTextField";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useReport from "../../data/Report/useReport";
import useReportMutations from "../../data/Report/useReportMutations";
import { CreateReportFormFields, ICreateReport } from "../../models/Reports/CreateReport";
import { IUpdateReport, UpdateReportFormFields } from "../../models/Reports/UpdateReport";
import { dateFieldsToText, textToDateFieldsJson } from "../../models/Reports/Report";
import { errorResolver } from "../../utils/errorResolver";

export function ReportForm() {
  const { reportId } = useParams();
  const { report } = useReport(reportId!);
  const { create, update } = useReportMutations();
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!reportId;

  const handleSubmit = (data: CreateReportFormFields | UpdateReportFormFields) => {
    setSubmitting(true);

    // The form edits the date fields as `key|Label` lines; the API stores JSON.
    const { dateFieldsText, ...rest } = data;
    const payload = { ...rest, dateFields: textToDateFieldsJson(dateFieldsText) };

    if (isEditing) {
      update.mutate(
        { ...payload, id: reportId! } as IUpdateReport,
        {
          onSettled: () => {
            setSubmitting(false);
          },
          onSuccess: () => {
            navigate(-1);
          },
        },
      );
    } else {
      create.mutate(
        payload as ICreateReport,
        {
          onSettled: () => {
            setSubmitting(false);
          },
          onSuccess: () => {
            navigate(-1);
          },
        },
      );
    }
  };

  useEffect(() => {
    if (isEditing) {
      if (!report.isLoading && report.data) {
        setInfo({
          name: report.data.title,
          desc: `Edit ${report.data.title}`,
          pageTitle: `Edit Report | ${report.data.title}`,
        });
      } else {
        setInfo({
          name: "Edit Report",
          desc: "Edit report details",
          pageTitle: "Edit Report",
        });
      }
    } else {
      setInfo({
        name: "New Report",
        desc: "Create a new report.",
        pageTitle: "New Report",
      });
    }
  }, [isEditing, report.isLoading, report.data]);

  if (isEditing && !reportId) {
    return <Navigate to="/reports" />;
  } else if (isEditing && report.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (isEditing && (report.isError || !report.data)) {
    return (
      <Alert
        severity="error"
        title="Error getting report"
        message={errorResolver(report.error)}
      />
    );
  } else {
    return (
      <div className="bg-white rounded shadow p-4">
        {submitting ? (
          <div className="py-8">
            <LoadingWheel />
          </div>
        ) : (
          <Form<CreateReportFormFields>
            onSubmit={handleSubmit}
            defaultValues={
              isEditing && report.data
                ? {
                    title: report.data.title,
                    rawSql: report.data.rawSql,
                    allowDates: report.data.allowDates,
                    dateFieldsText: dateFieldsToText(report.data.dateFields),
                  }
                : undefined
            }
            className="grid grid-cols-1 gap-y-4 md:w-3/4 md:grid-cols-2 md:gap-4"
          >
            {({
              register,
            }: UseFormReturn<CreateReportFormFields>) => (
              <>
                <div className="col-span-2 grid grid-cols-1 gap-y-4 md:gap-4 md:px-4 md:grid-cols-2">
                  <div className="md:col-span-2 border-b">
                    <p className="text-base font-medium text-gray-600 font-stick pb-2">
                      Report Information
                    </p>
                  </div>
                  <FormTextField
                    {...register("title", { required: true })}
                    placeholder="Sales Report"
                    label="Report Title"
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register("allowDates")}
                      className="rounded border-orange-600 focus:ring-orange-600"
                    />
                    <label className="text-sm text-black font-medium opacity-80">
                      Allow Date Parameters (legacy)
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-black font-medium opacity-80">
                      Date Fields
                    </label>
                    <textarea
                      {...register("dateFieldsText")}
                      placeholder={"Created|Created Date\nDismantled|Dismantled Date"}
                      className="w-full h-20 px-3 py-2 rounded border outline-none focus:drop-shadow-none focus:ring-2 !border-orange-600 focus:ring-orange-600 bg-white text-black font-mono text-sm"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      One <code>key|Label</code> per line. The key is bound into the SQL below as{" "}
                      <code>@DateField</code>; the label is what the reporting user picks from.
                      Leave blank for a report that offers no date filtering.
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Fill this in only once the SQL reads <code>@DateField</code> and treats a null{" "}
                      <code>@StartDate</code> as unbounded. Against the older both-dates-required
                      SQL it would offer a blank range that returns no rows.
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-black font-medium opacity-80">
                      SQL Query
                    </label>
                    <textarea
                      {...register("rawSql", { required: true })}
                      placeholder="SELECT * FROM table WHERE ..."
                      className="w-full h-32 px-3 py-2 rounded border outline-none focus:drop-shadow-none focus:ring-2 !border-orange-600 focus:ring-orange-600 bg-white text-black"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Pattern for a report with date fields. One branch per field keeps each
                      comparison able to use its index, which a CASE over the columns cannot:
                    </p>
                    <pre className="text-xs text-gray-700 mt-1 p-2 bg-gray-50 rounded border overflow-x-auto">
{`WHERE t.Deleted IS NULL
  AND ( @StartDate IS NULL
     OR (@DateField = 'Created'    AND t.Created      >= @StartDate AND t.Created      < DATEADD(day, 1, @EndDate))
     OR (@DateField = 'Dismantled' AND t.DismantledOn >= @StartDate AND t.DismantledOn < DATEADD(day, 1, @EndDate)) )
ORDER BY t.Created DESC
OPTION (RECOMPILE);`}
                    </pre>
                    <p className="text-xs text-gray-600 mt-1">
                      <code>OPTION (RECOMPILE)</code> is required, not decoration: without it one
                      cached plan has to satisfy every branch and the query scans.
                    </p>
                  </div>
                  <div className="col-span-2 mt-4 pt-4 grid grid-cols-4 gap-4 border-t">
                    <Button type="submit">
                      {isEditing ? "Update Report" : "Create Report"}
                    </Button>
                    <Button type="reset" variant="outlined">
                      Reset
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Form>
        )}
      </div>
    );
  }
}
